/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React, { useState }  from 'react';
import {
    bool, object, func, string,
} from 'prop-types';
import ReactMarkdown from 'react-markdown';
import Fade from '@material-ui/core/Fade';
import Dialog from '@material-ui/core/Dialog';
import Grid from '@material-ui/core/Grid';
import { makeStyles } from '@material-ui/core/styles';
import FlexibleShippingRadioButtons from './FlexibleShippingRadioButtons';

const useStyles = makeStyles((theme) => ({
    modalHeaderContainer: {
        width: '100%',
    },
    modalHeader: {
        padding: '10px 0px',
        boxShadow: ' 0px 3px 3px 0px rgba(47, 47, 47, 0.15)',
        textTransform: 'uppercase',
        color: '#000000',
        fontSize: '16px',
        textAlign: 'center',
        lineHeight: '24px',
    },
    modalHeaderNoText: {
        height: '44px !important',
    },
    modalContainer: {
        maxHeight: '100%',
        margin: '0px auto',
        width: '100%',
        alignItems: 'start',
        borderRadius: '0px',
        maxWidth: '670px',
        position: 'relative',
        overflowY: 'auto',
        [theme.breakpoints.up('sm')]: {
            top: '10% !important',
        },
        '& .MuiDialog-container': {
            height: 'auto',
            [theme.breakpoints.down(700)]: {
                height: '100%',
            },
        },
        '& .MuiDialog-paperWidthSm': {
            width: '100%',
            minHeight: 'calc(790px - 83px)',
            margin: '0px',
            [theme.breakpoints.down(700)]: {
                minHeight: '100%',
            },
        },
    },
    modalContainerV2: {
        [theme.breakpoints.up('sm')]: {
            top: '5% !important',
        },
        '& div': {
            alignItems: 'start',
            '&.MuiDrawer-paperAnchorBottom': { // paperAnchorBottom is the css for the mobile slideup drawer
                overflowX: 'hidden',
                overflowY: 'auto',
                '-webkit-tap-highlight-color': 'transparent',
            },
            [theme.breakpoints.down('sm')]: {
                '& .MuiDialog-paper': {
                    overflow: 'hidden',
                },
            },
            '& div': {
                maxHeight: '100%',
                height: '100%',
                width: '100%',
                margin: '0px',
                alignItems: 'start',
                borderRadius: '0px',
                maxWidth: '905px',
                position: 'relative',
                [theme.breakpoints.between(650, 700)]: { // this breakpoint is to target iphone 14 pro specifically
                    height: 'auto',
                },
                [theme.breakpoints.up('sm')]: {
                    height: 'auto',
                },
            },
        },
    },
    buttonCheckoutMobile: {
        border: 'none',
        borderRadius: '3px',
        width: '487px',
        height: '56px',
        fontSize: '18px',
        fontWeight: '700',
        fontFamily: 'Lato',
        fontStyle: 'normal',
        lineHeight: '26px',
        color: '#FFFFFF',
        backgroundColor: '#201648',
        padding: '15px 0px',
        margin: '24px',
        cursor: 'pointer',
        '&:hover, &:focus, &:active': {
            backgroundColor: '#201648',
            color: theme?.palette?.white,
        },
        [theme.breakpoints.down(600)]: {
            position: 'fixed',
            bottom: 0,
            width: '95%',
        },
    },

    buttonCheckout: {
        display: 'flex',
        justifyContent: 'space-around',
    },

    backToCalendarButton: {
        display: 'flex',
        marginLeft: '22px',
        marginTop: '22px',
        marginBottom: '12px',
        cursor: 'pointer',
        color: '#2F363D',
        [theme.breakpoints.down(600)]: {
            marginLeft: '10px',
        },
    },
    backToCalendarButtonText: {
        fontFamily: 'Lato',
        height: '12px',
        top: '3px',
        fontSize: '14px',
        fontWeight: 400,
        color: '#555555',
        textDecoration: 'underline',
    },
    titleContainer: {
        textAlign: 'center',
        fontFamily: 'lato',
        fontStyle: 'normal',
        color: '#1F1F1F',
        width: '487px',
        margin: '0 auto',
        [theme.breakpoints.down(600)]: {
            width: '95%',
        },
        '& p': {
            marginBottom: '0px',
            marginTop: '24px',
            fontFamily: 'lato',
            fontSize: '18px',
            lineHeight: '26px',
        },
        '& strong': {
            fontSize: '32px',
            lineHeight: '48px',
            [theme.breakpoints.down(600)]: {
                fontSize: '24px',
                lineHeight: '36px',
            },
        },
    },
    imageContainer: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
    },
    weatherBanner: {
        margin: '10px 35px 0px',
        [theme.breakpoints.down(400)]: {
            margin: '0px 30px !important',
        },
        [theme.breakpoints.down(376)]: {
            margin: '0px 23px !important',
        },
        [theme.breakpoints.down(361)]: {
            margin: '0px 25px !important',
        },
    },
    holidayBanner: {
        visibility: 'hidden',
        height: '60px',
        overflow: 'hidden',
    },
}));

const FlexibleShippingOptions = ({
    isOpen, handleFlexClose, dataObject, flexData, cmsData, flexAddToCart, weatherBanner, deviceType, pageType, brandId, flex, location, isNewDeliveryCalendarEnabledInCheckout,
}) => {
    const designatedBrand = brandId === '1001' || brandId === '1029' || brandId === '1010' || brandId === '1034' || brandId === '1038';
    const flexModalHeaderCopy = cmsData?.entries?.[0]?.flex_modal_header_copy || '';
    const holidayBannerVisible = localStorage.getItem('holidayBanner');
    const classes = useStyles();
    const [selectedDate, setDate] = useState(flexData.find((flexDate) => flexDate?.defaultOption === 'Y') || flexData?.[0]);
    const handleDateChange = (e) => {
        // The whole flex date object needs to be stored in state.
        setDate(flexData.find((flexDay) => flexDay.optionCode === e.target.value));
    };
    const handleOnClick = () => {
        flexAddToCart(selectedDate);
        handleFlexClose();
        return null;
    };
    const flexScreenTitle = flexData?.[0]?.flexMsg?.[6]?.msgText || 'Holiday Shipping Options';
    const imageUrl = cmsData?.entries?.[0]?.flex_banner_head_image?.url || 'https://images.contentstack.io/v3/assets/bltdd99f24e8a94d536/blt0cb6253d0865621a/65b1ef4f68334a2969c60287/2024_valentines_heart_v2.svg';
    const backButtonIcon = 'https://images.contentstack.io/v3/assets/bltdd99f24e8a94d536/blt513058fa097e5e54/chevron-left.svg';
    const flexModalContent = () => (
        <>
            <div className={classes.modalHeaderContainer}>
                {designatedBrand ? (
                    <div className={flexModalHeaderCopy ? `${classes.modalHeader} modalHeader` : `${classes.modalHeader} ${classes.modalHeaderNoText}`}>
                        {flexModalHeaderCopy ? <strong>{flexModalHeaderCopy}</strong> : ''}
                    </div>
                ) : (
                    <div className={`${classes.modalHeader} modalHeader`}>
                        <strong>SELECT DELIVERY DATE</strong>
                    </div>
                )}
            </div>
            <span
                className={classes.backToCalendarButton}
                onClick={handleFlexClose}
                role="presentation"
            >
                <img src={backButtonIcon} alt="cheveron_left_icon" />
                <div className={classes.backToCalendarButtonText}>Back to calendar</div>
            </span>
            <div className={classes.titleContainer}>
                <div className={classes.imageContainer}><img src={imageUrl} alt="Valentines Logo" /></div>
                <ReactMarkdown
                    escapeHtml={false}
                    source={flexScreenTitle}
                />
            </div>
            {weatherBanner && (
                <span className={classes.weatherBanner}>
                    {weatherBanner}
                </span>
            )}
            <div className={classes.radioButtonsDiv}>
                <FlexibleShippingRadioButtons flexData={flexData} cmsData={cmsData} dataObject={dataObject} flexAddToCart={flexAddToCart} handleDateChange={handleDateChange} deviceType={deviceType} selectedDate={selectedDate} pageType={pageType} />
                <Grid container className={classes.buttonCheckout}>
                    <button
                        onClick={() => { handleOnClick(); }}
                        className={classes.buttonCheckoutMobile}
                        type="button"
                        data-testid="confirmCheckout"
                    >
                        Save Delivery Date & Continue
                    </button>
                </Grid>
            </div>
            {holidayBannerVisible && (
                <span className={classes.holidayBanner} />
            )}
        </>
    );

    const handleShowPopUp = () => {
        if (flex?.showFlexPopup === 'N') {
            return handleOnClick();
        }
        return (
            <Dialog
                open={isOpen}
                transitionDuration={300}
                TransitionComponent={Fade}
                className={isNewDeliveryCalendarEnabledInCheckout && deviceType !== 'mobile' ? classes.modalContainerV2 : classes.modalContainer}
                aria-describedby="flex-options"
                hideBackdrop
            >
                {flexModalContent()}
            </Dialog>
        );
    };
    return handleShowPopUp();
};

FlexibleShippingOptions.propTypes = {
    isOpen: bool.isRequired,
    dataObject: object.isRequired,
    handleFlexClose: func.isRequired,
    flexData: object.isRequired,
    cmsData: object.isRequired,
    flexAddToCart: func.isRequired,
    weatherBanner: bool.isRequired,
    deviceType: string.isRequired,
    pageType: string,
    brandId: string.isRequired,
    flex: object.isRequired,
    isNewDeliveryCalendarEnabledInCheckout: bool.isRequired,
};
FlexibleShippingOptions.defaultProps = {
    pageType: 'product',
};

export default FlexibleShippingOptions;
